import { Box, DialogContent, Divider, Typography } from '@mui/material'
import { useEffect, useRef, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { endOfDay, startOfDay, subDays } from 'date-fns'
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'

import { getGroupTitle } from '../tanning-history.utils'

import DialogLayout from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.component'
import {
  CLOSE_TYPES,
  SIZES,
} from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.constants'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import Button from '@hapana/shared/web/components/shared/button/button.component'
import {
  BUTTON_SIZE,
  BUTTON_THEME,
  BUTTON_VARIANTS,
} from '@hapana/shared/web/components/shared/button/button.constants'
import Chip from '@hapana/shared/web/components/shared/chip/chip.component'
import { CHIP_SIZE, CHIP_VARIANT } from '@hapana/shared/web/components/shared/chip/chip.constants'
import AvatarComponent from '@hapana/shared/web/components/shared/avatar/avatar.component'
import { AVATAR_SIZE } from '@hapana/shared/web/components/shared/avatar/avatar.constants'
import Badge from '@hapana/shared/web/components/shared/badge/badge.component'
import { BADGE_ANCHOR_ORIGIN } from '@hapana/shared/web/components/shared/badge/badge.constants'
import FilterSmallIcon from '@hapana/shared/web/styles/assets/svgs/filter-small.svg'
import useSiteStore from '_stores/site.store'
import { useGetSiteById } from '_hooks/gqty/sites.hooks'
import { useEquipmentActivity } from '_hooks/equipments.hooks'
import { useClientsByIds } from '_hooks/clients.hooks'
import Loading from '@hapana/shared/web/components/shared/loading/loading.component'

import HistoryFilterDialog from './history-filter-dialog.component'
import { styles } from './history.styles'
const History = () => {
  const { t } = useTranslation(['checkIn'])
  const { closeDialog } = useDialog()
  const [filterOpen, setFilterOpen] = useState(false)
  const siteId = useSiteStore((state) => state.currentSite?.siteId)
  const { data: siteData } = useGetSiteById(siteId)
  const siteTimezone = siteData?.timezone

  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    clientId: null,
  })

  const timezoneInitialized = useRef(false)
  const baselineDatesRef = useRef({ startDate: null, endDate: null })
  useEffect(() => {
    if (!siteTimezone || timezoneInitialized.current) return
    timezoneInitialized.current = true
    const tz = siteTimezone
    const start = zonedTimeToUtc(startOfDay(subDays(utcToZonedTime(new Date(), tz), 7)), tz)
    const end = zonedTimeToUtc(endOfDay(utcToZonedTime(new Date(), tz)), tz)
    baselineDatesRef.current = { startDate: start, endDate: end }
    setFilters((prev) => ({ ...prev, startDate: start, endDate: end }))
  }, [siteTimezone])

  const { data: activityGroups = [], isFetching: isLoading } = useEquipmentActivity(
    siteId,
    filters.startDate ? filters.startDate.toISOString() : null,
    filters.endDate ? filters.endDate.toISOString() : null,
    filters.clientId ?? undefined
  )

  const allClientIds = useMemo(
    () =>
      Array.from(
        new Set(
          (activityGroups || []).flatMap((group) =>
            (group?.items || []).map((item) => item.clientId)
          )
        )
      ).filter(Boolean),
    [activityGroups]
  )
  const { data: clientsById, isLoading: isLoadingClients } = useClientsByIds(allClientIds, siteId)

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (filters.clientId) count++
    const { startDate: baseStart, endDate: baseEnd } = baselineDatesRef.current
    if (
      baseStart &&
      baseEnd &&
      (filters.startDate?.getTime() !== baseStart.getTime() ||
        filters.endDate?.getTime() !== baseEnd.getTime())
    )
      count++
    return count || null
  }, [filters])

  const handleOpenFilter = () => setFilterOpen(true)

  const formatGroupTitle = (dateString) =>
    getGroupTitle(
      dateString,
      t,
      {
        todayKey: 'checkIn:equipments.history.today',
        yesterdayKey: 'checkIn:equipments.history.yesterday',
      },
      siteTimezone
    )

  return (
    <DialogLayout
      closeType={CLOSE_TYPES.ICON}
      closeAction={closeDialog}
      title={t('checkIn:equipments.history.title')}
      size={SIZES.SM}
      hasBackLabel
    >
      <DialogContent sx={styles.dialogContent}>
        <HistoryFilterDialog
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          currentFilters={filters}
          siteId={siteId}
          siteTimezone={siteTimezone}
          onApply={(newFilters) => setFilters(newFilters)}
        />
        <Box sx={styles.content}>
          <Box sx={styles.filterButtonRow}>
            <Typography typography="header2" color="gray.80">
              {t('checkIn:equipments.history.title')}
            </Typography>
            <Badge
              badgeContent={activeFilterCount}
              anchorOrigin={BADGE_ANCHOR_ORIGIN.TOP_RIGHT}
              color="primary"
              sx={styles.badgePosition}
            >
              <Button
                variant={BUTTON_VARIANTS.ICON}
                icon={FilterSmallIcon}
                size={BUTTON_SIZE.MEDIUM}
                stroke
                onClick={handleOpenFilter}
              />
            </Badge>
          </Box>

          {isLoading || isLoadingClients ? (
            <Box sx={styles.loadingContainer}>
              <Loading />
            </Box>
          ) : (
            (activityGroups || []).map((group, index) => (
              <Box key={group.date} sx={styles.groupContainer}>
                <Box sx={styles.groupHeader}>
                  <Typography typography="medium" color="gray.64">
                    {formatGroupTitle(group.date)}
                  </Typography>
                  <Typography typography="medium" color="gray.64">
                    {t('checkIn:equipments.history.clients', { count: group.items?.length || 0 })}
                  </Typography>
                </Box>

                <Box sx={styles.list}>
                  {(group.items || []).map((item) => {
                    const client = clientsById?.[item.clientId]
                    const clientUrl =
                      client && siteId ? `/s/${siteId}/clients/${client.id}` : undefined

                    return (
                      <Box
                        key={`${group.date}-${item.clientId}-${item.equipmentId}`}
                        sx={styles.listItem}
                      >
                        <Box sx={styles.personInfo}>
                          {client && (
                            <AvatarComponent
                              item={{
                                src: client?.profileImage?.url || 'https://via.placeholder.com/80',
                                alt: `${client.firstName} ${client.lastName || ''}`,
                              }}
                              size={AVATAR_SIZE.SEARCH_RESULT}
                            />
                          )}
                          <Box sx={styles.personDetails}>
                            <Typography typography="header3" color="gray.100">
                              {client?.firstName
                                ? `${client.firstName} ${client.lastName || ''}`
                                : t('checkIn:equipments.manageEquipments.test')}
                            </Typography>
                            <Typography typography="medium" color="gray.64" sx={styles.email}>
                              {client?.email}
                            </Typography>
                            <Typography typography="medium" color="gray.64">
                              {t('checkIn:equipments.history.tannedFor', {
                                minutes: item.durationMinutes,
                              })}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={styles.actions}>
                          <Chip
                            label={item.equipmentName}
                            variant={CHIP_VARIANT.CONTAINED}
                            size={CHIP_SIZE.MEDIUM}
                          />
                          <Button
                            variant={BUTTON_VARIANTS.OUTLINED}
                            label={t('checkIn:equipments.detail.viewClientRecord')}
                            size={BUTTON_SIZE.SMALL}
                            theme={BUTTON_THEME.GRAY}
                            href={clientUrl}
                          />
                        </Box>
                      </Box>
                    )
                  })}
                </Box>
                {index !== activityGroups.length - 1 && <Divider sx={styles.divider} />}
              </Box>
            ))
          )}
          {!isLoading && activityGroups.length === 0 && (
            <Typography typography="medium" color="gray.64" sx={styles.topMargin}>
              {t('checkIn:equipments.history.noPreviousTanningFound')}
            </Typography>
          )}
        </Box>
      </DialogContent>
    </DialogLayout>
  )
}

export default History
